import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { ConfigSearchEntry, getConfigSearchIndex, loadServicingInfoEntries } from './searchIndex'

const RESULT_LIMIT = 40

type Scored = { entry: ConfigSearchEntry; rank: number; at: number }

/**
 * Search box for the Admin > Configuration page.
 *
 * Matches a setting anywhere in the 40-odd configuration sections and hands the
 * chosen entry back so the page can switch to its section and scroll to it.
 */
export const ConfigSearch = ({ onJump }: { onJump: (entry: ConfigSearchEntry) => void }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const fetchedServicingInfo = useRef(false)

  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [servicingInfo, setServicingInfo] = useState<ConfigSearchEntry[]>([])

  const staticEntries = useMemo(() => getConfigSearchIndex(), [])
  const entries = useMemo(() => [...staticEntries, ...servicingInfo], [staticEntries, servicingInfo])

  // Servicing Information field labels come from the backend; pull them once.
  const loadDynamic = () => {
    if (fetchedServicingInfo.current) return
    fetchedServicingInfo.current = true
    loadServicingInfoEntries().then(setServicingInfo)
  }

  const results = useMemo<ConfigSearchEntry[]>(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    const scored: Scored[] = []
    for (const entry of entries) {
      const label = entry.label.toLowerCase()
      const at = label.indexOf(q)
      const inLabel = at !== -1
      if (!inLabel && `${entry.menu} ${entry.section || ''} ${entry.keywords || ''}`.toLowerCase().indexOf(q) === -1)
        continue
      // Whole sections first, then a label that starts with the query, then the rest.
      let rank = 4
      if (inLabel) rank = at === 0 ? (entry.label.toLowerCase() === q ? 0 : 1) : 2
      else rank = 3
      if (entry.id.startsWith('menu-') && inLabel) rank = -1
      scored.push({ entry, rank, at: inLabel ? at : 999 })
    }
    scored.sort((a, b) => a.rank - b.rank || a.at - b.at || a.entry.label.localeCompare(b.entry.label))
    return scored.slice(0, RESULT_LIMIT).map((s) => s.entry)
  }, [query, entries])

  useEffect(() => setActiveIndex(0), [query, open])

  // Close when clicking outside.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Keep the highlighted row in view.
  useLayoutEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, results])

  const go = (entry: ConfigSearchEntry) => {
    setOpen(false)
    onJump(entry)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActiveIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (results[activeIndex]) go(results[activeIndex])
    } else if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
    }
  }

  const showResults = open && query.trim().length > 0

  return (
    <div ref={containerRef} className="relative w-full sm:w-96">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          ref={inputRef}
          id="configuration-search"
          type="text"
          value={query}
          placeholder="Search all configuration settings…"
          className="block w-full rounded-full border border-slate-200 bg-slate-50 py-1.5 pl-8 pr-8 text-sm text-gray-800 placeholder:text-gray-400 focus:border-sky-200 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-200"
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            loadDynamic()
          }}
          onFocus={() => {
            setOpen(true)
            loadDynamic()
          }}
          onKeyDown={onKeyDown}
        />
        {query && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
            onMouseDown={(e) => {
              e.preventDefault()
              setQuery('')
              inputRef.current?.focus()
            }}
            aria-label="Clear"
          >
            <XMarkIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {showResults && (
        <div
          ref={listRef}
          className="absolute left-0 z-[60] mt-2 max-h-[60vh] w-[min(34rem,calc(100vw-2rem))] overflow-auto rounded-xl border border-slate-100 bg-white py-1 shadow-2xl"
        >
          {results.length === 0 && <div className="px-4 py-3 text-sm text-gray-500">No configuration matches “{query}”.</div>}
          {results.map((entry, idx) => (
            <div
              key={entry.id}
              data-idx={idx}
              className={`flex cursor-pointer items-center justify-between gap-3 px-4 py-2 text-sm ${
                idx === activeIndex ? 'bg-shade-blue/10' : 'hover:bg-gray-50'
              }`}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={(e) => {
                e.preventDefault()
                go(entry)
              }}
            >
              <span className="truncate font-medium text-gray-800">{entry.label}</span>
              <span className="shrink-0 truncate rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-gray-500">
                {entry.section && entry.section !== 'Tab' ? `${entry.menu} › ${entry.section}` : entry.menu}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
